import { buildVehicleMarkerIcon } from '@/utils/vehicleMapIcon';

export const LIVE_VEHICLE_Z = 1000;
export const ROUTE_LINE_Z = 40;
export const ORIGIN_Z = 4;
export const DESTINATION_Z = 4;

/** Clear markers and overlay shapes (circles, polylines). */
export function clearMapLayers(markersRef, overlaysRef) {
  if (markersRef?.current) {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
  }
  if (overlaysRef?.current) {
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];
  }
}

function extendBounds(bounds, position) {
  if (bounds && position?.lat != null && position?.lng != null) {
    bounds.extend(position);
  }
}

/** Small origin / destination dot. */
export function addRouteDotMarker({
  map,
  position,
  color,
  title,
  markersRef,
  bounds,
  zIndex = ORIGIN_Z,
}) {
  if (!map || !window.google?.maps) return null;
  const marker = new window.google.maps.Marker({
    position,
    map,
    title,
    icon: {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#0a3042',
      strokeWeight: 2,
      scale: 7,
    },
    zIndex,
  });
  markersRef.current.push(marker);
  extendBounds(bounds, position);
  return marker;
}

/** Glowing rings + prominent vehicle pin for live GPS. */
export function addLiveVehicleLayers({
  map,
  position,
  plate,
  heading,
  title,
  markersRef,
  overlaysRef,
  onClick,
  liveLabel = 'LIVE',
}) {
  if (!map || !window.google?.maps) return null;

  const outerRing = new window.google.maps.Circle({
    map,
    center: position,
    radius: 180,
    fillColor: '#36e961',
    fillOpacity: 0.14,
    strokeColor: '#36e961',
    strokeOpacity: 0.75,
    strokeWeight: 2.5,
    zIndex: LIVE_VEHICLE_Z - 2,
  });

  const innerRing = new window.google.maps.Circle({
    map,
    center: position,
    radius: 55,
    fillColor: '#2563eb',
    fillOpacity: 0.22,
    strokeColor: '#0a3042',
    strokeOpacity: 0.9,
    strokeWeight: 2,
    zIndex: LIVE_VEHICLE_Z - 1,
  });

  overlaysRef.current.push(outerRing, innerRing);

  const vehicleIcon = buildVehicleMarkerIcon({
    plate,
    heading,
    showPlate: true,
    highlighted: true,
    liveLabel,
  });

  const marker = new window.google.maps.Marker({
    position,
    map,
    title: title || plate,
    icon: vehicleIcon || undefined,
    zIndex: LIVE_VEHICLE_Z,
  });

  if (onClick) marker.addListener('click', () => onClick(marker));
  markersRef.current.push(marker);
  return marker;
}

/** Dashed line from live vehicle toward destination. */
export function addVehicleToDestinationLine({ map, from, to, overlaysRef }) {
  if (!map || !from || !to || !window.google?.maps) return;

  const line = new window.google.maps.Polyline({
    path: [from, to],
    geodesic: true,
    strokeColor: '#2563eb',
    strokeOpacity: 0,
    strokeWeight: 0,
    icons: [
      {
        icon: {
          path: 'M 0,0 0,1',
          strokeColor: '#2563eb',
          strokeOpacity: 0.85,
          scale: 4,
        },
        offset: '0',
        repeat: '16px',
      },
    ],
    map,
    zIndex: ROUTE_LINE_Z,
  });
  overlaysRef.current.push(line);
}

/**
 * Draw all markers for active transport services on a Google Map.
 * @returns {{ hasLiveGps: boolean, livePositions: Array<{lat,lng}> }}
 */
export function syncActiveServicesOnMap({
  map,
  activeServices,
  markersRef,
  overlaysRef,
  bounds,
  getCurrentGps,
  getPickupGps,
  getDeliveryGps,
  getPlate,
  t,
  onLiveVehicleClick,
}) {
  clearMapLayers(markersRef, overlaysRef);

  let hasValidLocations = false;
  const livePositions = [];

  activeServices.forEach((service) => {
    const cur = getCurrentGps(service);
    const pickup = getPickupGps(service);
    const delivery = getDeliveryGps(service);
    const plate = getPlate(service) || '—';

    if (pickup) {
      addRouteDotMarker({
        map,
        position: { lat: pickup.lat, lng: pickup.lng },
        color: '#22c55e',
        title: `${plate} — ${t('gps_map_origin', { defaultValue: 'Origin' })}`,
        markersRef,
        bounds,
        zIndex: ORIGIN_Z,
      });
    }

    if (delivery) {
      addRouteDotMarker({
        map,
        position: { lat: delivery.lat, lng: delivery.lng },
        color: '#ef4444',
        title: `${plate} — ${t('gps_map_destination', { defaultValue: 'Destination' })}`,
        markersRef,
        bounds,
        zIndex: DESTINATION_Z,
      });
    }

    if (!cur) return;

    const position = { lat: cur.lat, lng: cur.lng };
    livePositions.push(position);
    extendBounds(bounds, position);
    hasValidLocations = true;

    if (delivery) {
      addVehicleToDestinationLine({
        map,
        from: position,
        to: { lat: delivery.lat, lng: delivery.lng },
        overlaysRef,
      });
    }

    const vehicleTitle = `${plate} — ${t('gps_map_live_vehicle', { defaultValue: 'Live vehicle' })}`;
    const onClick = onLiveVehicleClick
      ? (marker) => onLiveVehicleClick(service, position, marker)
      : undefined;

    addLiveVehicleLayers({
      map,
      position,
      plate,
      heading: Number.isFinite(Number(cur.heading)) ? Number(cur.heading) : 0,
      title: vehicleTitle,
      markersRef,
      overlaysRef,
      onClick,
      liveLabel: t('gps_map_live_badge', { defaultValue: 'LIVE' }),
    });
  });

  return {
    hasValidLocations,
    hasLiveGps: livePositions.length > 0,
    livePositions,
  };
}

/** Fit map to markers; zoom in on a single live vehicle. */
export function fitMapToMarkers(map, bounds, { livePositions = [], maxZoom = 14 } = {}) {
  if (!map || !window.google?.maps) return;

  if (livePositions.length === 1) {
    map.setCenter(livePositions[0]);
    map.setZoom(Math.max(map.getZoom() || 0, 13));
    return;
  }

  if (bounds && !bounds.isEmpty()) {
    map.fitBounds(bounds, { top: 56, right: 56, bottom: 56, left: 56 });
    const listener = window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
    });
    window.setTimeout(() => window.google.maps.event.removeListener(listener), 500);
  }
}
